import { Grid } from "@mui/material";
import { useState } from "react";
import { useDefaultValues } from "../../common/defaultValues";
import { createEntityStore } from "../../common/entityStore";
import { propBoolField, propEnumField, propNumberField, propTextField } from "../../common/entityStoreForm";
import { propCurrency, propInt, propLength, propLengthMax, propMaxDecimalPlaces, propMaxValue, propMinValue, propNotRegex, propOnlyDigits, propRegex, propRequired, propValidValue, propValidEmail } from "../../common/entityStoreValidators";
import { useCollectionAgencies, useTransactionCodes, useWarehouses } from "../../common/enum";
import { PageTitle } from "../../layout/PageTitle";
import { DefaultValuesDto } from "./types";
import { InputSizedSaveButtonSx } from "../../common/form";
import { EntityStoreSaveButton } from "../../common/SaveButton";
import { StoreWrapper } from "../../common/store";

export function DefaultValuesPage() {
    const [useStore] = useState(() => createEntityStore<DefaultValuesEditDto>({
        path: "defaultValues", data: {},
        onSaved() {
            useDefaultValues.getState().reset();
            useDefaultValues.getState().init();
        }, propValidators: [
            propValidValue("fstPstPct", "fstPstPctFormatted"), propRequired("fstPstPct"), propMaxDecimalPlaces("fstPstPct", 2), propMaxValue("fstPstPct", 99.99), propMinValue("fstPstPct", 0),
            propValidValue("fstPstAmt", "fstPstAmtFormatted"), propRequired("fstPstAmt"), propCurrency("fstPstAmt"), propMinValue("fstPstAmt", 0),
            propValidValue("nrmPstPct", "nrmPstPctFormatted"), propRequired("nrmPstPct"), propMaxDecimalPlaces("nrmPstPct", 2), propMaxValue("nrmPstPct", 99.99), propMinValue("nrmPstPct", 0),
            propValidValue("nrmPstAmt", "nrmPstAmtFormatted"), propRequired("nrmPstAmt"), propCurrency("nrmPstAmt"), propMinValue("nrmPstAmt", 0),

            propRequired("currency"), propLength("currency", 3, 3), propRequired("currency"),
            propValidValue("retCnt", "retCntFormatted"), propRequired("retCnt"), propMinValue("retCnt", 1), propInt("retCnt"),

            propRequired("dummyCust"), propLength("dummyCust", 7, 7), propOnlyDigits("dummyCust"),
            propRegex("dummyCust", /^0/, "First digit cannot be 0."),
            propNotRegex("dummyCust", /^.0*$/, "Non-first digits must be 0."),
            propRequired("numberFormat"), propLengthMax("numberFormat", 50),

            propValidValue("boxWt", "boxWtFormatted"), propRequired("boxWt"), propMinValue("boxWt", 0), propMaxDecimalPlaces("boxWt", 2),
            propValidValue("envelopeWt", "envelopeWtFormatted"), propRequired("envelopeWt"), propMinValue("envelopeWt", 0), propMaxDecimalPlaces("envelopeWt", 2),

            propValidValue("packCost", "packCostFormatted"), propRequired("packCost"), propCurrency("packCost"), propMinValue("packCost", 0),
            propRequired("defCa"),

            propValidValue("mailId", "mailIdFormatted"), propRequired("mailId"), propMinValue("mailId", 0), propInt("mailId"),

            propValidValue("maxDunLvl", "maxDunLvlFormatted"), propRequired("maxDunLvl"), propMinValue("maxDunLvl", 1), propInt("maxDunLvl"),
            propValidValue("euroRate", "euroRateFormatted"), propRequired("euroRate"), propMinValue("euroRate", 0), propMaxDecimalPlaces("euroRate", 6),

            propLengthMax("address", 20),
            propLengthMax("addressMale", 20),
            propLengthMax("addressFemale", 20),
            propLengthMax("addressLong", 50),
            propLengthMax("addressLongMale", 50),
            propLengthMax("addressLongFemale", 50),

            propValidEmail("businessCenterEmailAddress"),
        ],
    }));

    return <>
        <PageTitle title="Default values - General" />
        <Grid container columnSpacing={4} alignItems="start">
            <Grid item container columnSpacing={2} xs={4}>
                <Grid item xs={6}>{propNumberField(useStore)("fstPstPct", "fstPstPctFormatted", "First shipment postage VAT", { unit: "%", disabled: true })}</Grid>
                <Grid item xs={6}>{propNumberField(useStore)("nrmPstPct", "nrmPstPctFormatted", "Regular shipment postage VAT", { unit: "%" })}</Grid>
                <Grid item xs={6}>{propNumberField(useStore)("fstPstAmt", "fstPstAmtFormatted", "First shipment postage", { isCurrency: true })}</Grid>
                <Grid item xs={6}>{propNumberField(useStore)("nrmPstAmt", "nrmPstAmtFormatted", "Regular shipment postage", { isCurrency: true })}</Grid>
                <Grid item xs={6}>{propTextField(useStore)("currency", "Currency code")}</Grid>
                <Grid item xs={6}>{propNumberField(useStore)("retCnt", "retCntFormatted", "Delivery stop after", { unit: "consecutive returns" })}</Grid>
                <Grid item xs={6}>{propTextField(useStore)("dummyCust", "Dummy customer number")}</Grid>
                <Grid item xs={6}>{propTextField(useStore)("numberFormat", "Float number format")}</Grid>
                <Grid item xs={6}>{propNumberField(useStore)("boxWt", "boxWtFormatted", "Box weight", { unit: "g" })}</Grid>
                <Grid item xs={6}>{propNumberField(useStore)("envelopeWt", "envelopeWtFormatted", "Envelope weight", { unit: "g" })}</Grid>
                <Grid item xs={6}>{propNumberField(useStore)("packCost", "packCostFormatted", "Base packing cost", { isCurrency: true })}</Grid>
                <Grid item xs={6}>{propEnumField(useStore)("defCa", "Default CA", useCollectionAgencies)}</Grid>
                <Grid item xs={6}>{propNumberField(useStore)("mailId", "mailIdFormatted", "Default Mail ID")}</Grid>
                <Grid item xs={6}>{propNumberField(useStore)("maxDunLvl", "maxDunLvlFormatted", "Max dunning level")}</Grid>
                <Grid item xs={6}>{propNumberField(useStore)("euroRate", "euroRateFormatted", "EUR exchange rate", { isCurrency: true })}</Grid>
                <Grid item xs={6}>{propNumberField(useStore)("allowedPaymentMethodLimit", "allowedPaymentMethodLimitFormatted", "Allowed payment method limit", { isCurrency: true })}</Grid>
                <Grid item xs={6}>{propBoolField(useStore)("showEurAr", "Show EUR in A/R")}</Grid>
                <Grid item xs={6}>{propBoolField(useStore)("ret1Rs", "Return 1st RS status")}</Grid>
                <Grid item xs={6}>{propNumberField(useStore)("codRoundingThreshold", "codRoundingThresholdFormatted", "COD rounding threshold", { isCurrency: true })}</Grid>
            </Grid>
            <Grid item container columnSpacing={2} xs={4}>
                <Grid item xs={6}>{propTextField(useStore)("address", "Salutation short")}</Grid>
                <Grid item xs={6}>{propTextField(useStore)("addressLong", "Salutation long")}</Grid>
                <Grid item xs={6}>{propTextField(useStore)("addressMale", "Salutation short - male")}</Grid>
                <Grid item xs={6}>{propTextField(useStore)("addressLongMale", "Salutation long - male")}</Grid>
                <Grid item xs={6}>{propTextField(useStore)("addressFemale", "Salutation short - female")}</Grid>
                <Grid item xs={6}>{propTextField(useStore)("addressLongFemale", "Salutation long - female")}</Grid>
                <Grid item xs={6}>{propEnumField(useStore)("reservationWarehouseId", "Reservation warehouse", useWarehouses)}</Grid>
                <Grid item xs={6}>{propEnumField(useStore)("reservationTransactionCodeId", "Reservation transaction code", useTransactionCodes)}</Grid>
                <Grid item xs={6}>{propEnumField(useStore)("shipmentWarehouseId", "Shipment warehouse", useWarehouses)}</Grid>
                <Grid item xs={6}>{propEnumField(useStore)("shipmentTransactionCodeId", "Shipment transaction code", useTransactionCodes)}</Grid>
                <Grid item xs={6}>{propEnumField(useStore)("customerReturnWarehouseId", "Customer return warehouse", useWarehouses)}</Grid>
                <Grid item xs={6}>{propEnumField(useStore)("customerReturnTransactionCodeId", "Customer return transaction code", useTransactionCodes)}</Grid>
                <Grid item xs={6}>{propEnumField(useStore)("postalReturnWarehouseId", "Postal return warehouse", useWarehouses)}</Grid>
                <Grid item xs={6}>{propEnumField(useStore)("postalReturnTransactionCodeId", "Postal return transaction code", useTransactionCodes)}</Grid>
                <Grid item xs={6}>{propBoolField(useStore)("isTmOrderConfirmationRequired", "TM order confirmation")}</Grid>
                <StoreWrapper useStore={() => useStore(p => p.clientData.isTmOrderConfirmationRequired)}>{isTmOrderConfirmationRequired => <>
                    <Grid item xs={6}>{propBoolField(useStore)("woOnlyUnconfirmed", "WO only unconfirmed", { disabled: !isTmOrderConfirmationRequired })}</Grid>
                </>}</StoreWrapper>
                <Grid item xs={6}>{propBoolField(useStore)("ofrIn1Rs", "Allow offer in 1st RS")}</Grid>
                <Grid item xs={6}>{propBoolField(useStore)("allowComgatePaymentLinks", "Allow Comgate payment links")}</Grid>
            </Grid>
            <Grid item container xs={2} columnSpacing={2}>
                <Grid item xs={12}>{propTextField(useStore)("businessCenterEmailAddress", "Business center email address")}</Grid>
                <Grid item xs={12}>{propTextField(useStore)("overweightOrderEmailSubjectText", "Overweight order email subject text")}</Grid>
                <Grid item xs={12}>{propTextField(useStore)("overweightOrderEmailText", "Overweight order email text", { multiline: true, rows: 15 })}</Grid>
            </Grid>
            <Grid item container xs={12} mt={4} justifyContent="flex-end">
                <EntityStoreSaveButton useStore={useStore} sx={InputSizedSaveButtonSx} disableEnter={true} />
            </Grid>
        </Grid>
    </>;
}

interface DefaultValuesEditDto extends Partial<DefaultValuesDto> {
    fstPstPctFormatted?: string;
    fstPstAmtFormatted?: string;
    nrmPstPctFormatted?: string;
    nrmPstAmtFormatted?: string;
    retCntFormatted?: string;
    boxWtFormatted?: string;
    envelopeWtFormatted?: string;
    packCostFormatted?: string;
    maxVatDiscFormatted?: string;
    mailIdFormatted?: string;
    euroRateFormatted?: string;
    maxDunLvlFormatted?: string;
    allowedPaymentMethodLimitFormatted?: string;
    codRoundingThresholdFormatted?: string;
}
